import { catchErrors } from 'puffy-core/error'
import { validateEmail } from 'puffy-core/validate'
import { IonPage, useIonToast, useIonModal } from '@ionic/react'
import React, { useState, useEffect, useRef } from 'react'
import Button from '@mui/material/Button'
import OutlinedInput from '@mui/material/OutlinedInput'
import InputLabel from '@mui/material/InputLabel'
import InputAdornment from '@mui/material/InputAdornment'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import CssBaseline from '@mui/material/CssBaseline'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import { Link, useHistory, useLocation } from 'react-router-dom'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import Typography from '@mui/material/Typography'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { Helmet } from 'react-helmet'
import { LOGIN_PATH, SIGNUP_PATH, FORGOT_PWD_PATH, CONFIRM_CODE_PATH, RESET_PWD_PATH, TERMS_PDF_PATH } from '../path'
import { Auth } from '../auth'
import Toast from '../utils/Toast'
import ProgressBar from '../components/ProgressBar'
import Logo from '../components/Logo'
import { isInstallable } from '../utils/browser'
import InstallAppMessage from '../components/InstallAppMessage'
import PasswordRules from '../components/PasswordRules'
import {
	SIGNUP_SUB_KEY, SIGNUP_EMAIL_KEY, CONFIRM_NEXT_KEY, RESEND_COOLDOWN_SEC, UNVERIFIED_EMAIL_ERROR, USER_EXISTS_ERRORS,
	get_password_issues, errors_to_message, has_error, store_confirm_context, resend_code, recover_existing_account, wait_for_auto_sign_in
} from '../utils/accountRecovery'
import './LoginSignup.css'

const PRIVACY_POLICY_PATH = 'https://www.goannaag.com.au/privacy-policy-mojave'
const HOME_PAGE = '/'
function Copyright(props: any) {
	return (
		<Typography variant="body2" color="text.secondary" align="center" marginTop="60px" {...props}>
			{'Copyright © Goanna AG '}
			{new Date().getFullYear()}
			{'.'}
		</Typography>
	)
}

function ErrorMessage(props: any) {
	const inputMsg = `${props.children||''}`
	const msg = inputMsg.indexOf('higher limit is required') >= 0
		? 'Sorry, we have received too many applications to sign up to WAND. We will notify you as soon this issue is resolved.'
		: inputMsg
	return (
		<Typography variant="body2" color="error.main" {...props}>
			{msg}
		</Typography>
	)
}

const theme = createTheme()

export enum Modes {
	login,
	signup,
	forgotpwd,
	resetpwd,
	codeconfirm
}

const login = (username:string, password:string, history: any, setError:Function, setProcessing: Function, setIncorrectEmail: Function, setIncorrectPwd: Function, redirect_url?:string) => catchErrors((async () => {
	const email = (username||'').toLowerCase().trim()
	if (!email) {
		setIncorrectEmail(true)
		setError('Missing required email')
	} else if (!validateEmail(email)) {
		setIncorrectEmail(true)
		setError(`Invalid email format`) 
	} else if (!password) {
		setIncorrectPwd(true)
		setError('Missing required password')
	} else {
		// Doc: https://docs.amplify.aws/lib/auth/emailpassword/q/platform/js/#sign-in
		const [userErrors] = await catchErrors(Auth.signIn(email, password))

		// setError('Exceeded daily email limit for the operation or the account. If a higher limit is required, please configure your user pool to use your own Amazon SES configuration for sending email.')

		if (userErrors) {
			if (userErrors.some((e:any) => e.message && e.message.indexOf('is not confirm') >= 0))
				await resendConfirmSignup(email, history, setError, setProcessing)
			else {
				let errMsg = userErrors.map((e:any) => e.message).join(' ') || ''
				setError(errMsg)
			}
		} else
			history.push(redirect_url || HOME_PAGE)
	}

	setProcessing(false)
})())

const signup = (username:string, password:string, confirmPassword:string, history: any, setError:Function, setProcessing: Function, setIncorrectEmail:Function, setIncorrectPwd: Function, setIncorrectPwdConfirm: Function, toast: Toast, redirect_url?:string) => catchErrors((async () => {
	const email = (username||'').toLowerCase().trim()
	if (!email) {
		setIncorrectEmail(true)
		setError('Missing required email')
	} else if (!validateEmail(email)) {
		setIncorrectEmail(true)
		setError(`Invalid email format`) 
	} else if (!password) {
		setIncorrectPwd(true)
		setError('Missing required password')
	} else if (get_password_issues(password).length) {
		setIncorrectPwd(true)
		setError(`Your password is missing: ${get_password_issues(password).join(', ')}`)
	} else if (password !== confirmPassword) {
		setIncorrectPwdConfirm(true)
		setError(`Failed to confirm password`)
	} else {
		const email = (username||'').toLowerCase().trim()
		/**
		 * Doc: https://docs.amplify.aws/lib/auth/emailpassword/q/platform/js/#sign-up
		 * user: {
		 * 		user: {
		 * 			username: 'example@gmail.com',
		 * 			pool: {
		 * 				userPoolId: 'ap-southeast-2_abcdefg',
		 * 				clientId: '123456789'
		 * 			}
		 * 		},
		 * 		userConfirmed: false,
		 * 		userSub: '1234-5678-91011-1213',
		 * 		codeDeliveryDetails: {
		 * 			AttributeName: 'email',
		 * 			DeliveryMedium: 'EMAIL',
		 * 			Destination: 'e***@***'
		 * 		}
		 * }
		 */
		const [userErrors, user] = await catchErrors(Auth.signUp({
			username: email,
			password,
			attributes: {
				email
			},
			autoSignIn: { // optional - enables auto sign in after user is confirmed
				enabled: true
			}
		}))

		if (userErrors && has_error(userErrors, USER_EXISTS_ERRORS))
			await recover_existing_account(email, password, history, setError, toast, redirect_url)
		else if (userErrors) {
			let errMsg = userErrors.map((e:any) => e.message).join(' ') || ''
			if (errMsg.indexOf('assword did not conform with policy') >= 0) {
				setIncorrectPwd(true)
				setIncorrectPwdConfirm(true)
			} else if (errMsg.indexOf('validation error detected') >= 0 || errMsg.indexOf('validation errors detected') >= 0) {
				errMsg = 'Invalid password'
				setIncorrectPwd(true)
				setIncorrectPwdConfirm(true)
			}
			setError(errMsg)
		}
		else if (!user || !user.userSub)
			setError('Internal error: Server did not return user ID. Please contact support.')
		else {
			localStorage.setItem(SIGNUP_SUB_KEY, user.userSub)
			localStorage.setItem(SIGNUP_EMAIL_KEY, email)
			localStorage.setItem(CONFIRM_NEXT_KEY, 'home')
			history.push(CONFIRM_CODE_PATH)
		}
	}

	setProcessing(false)
})())

const confirmSignup = (code:string, email:string, history: any, setError:Function, setProcessing: Function, toast: Toast, redirect_url?:string) => catchErrors((async () => {
	if (!code)
		setError(`Code is required`)
	else {
		const username = (email||'').toLowerCase().trim() || localStorage.getItem(SIGNUP_SUB_KEY)
		if (!username)
			setError(`Email is required`)
		else {
			const next = localStorage.getItem(CONFIRM_NEXT_KEY)
			const auto_sign_in = next == 'home' ? wait_for_auto_sign_in() : null
			// Doc: https://docs.amplify.aws/lib/auth/emailpassword/q/platform/js/#confirm-sign-up
			const [userErrors] = await catchErrors(Auth.confirmSignUp(username, code.trim()))

			if (userErrors) {
				let errMsg = userErrors.map((e:any) => e.message).join(' ') || ''
				if (errMsg.indexOf('validation error detected') >= 0 || errMsg.indexOf('validation errors detected') >= 0)
					errMsg = 'Invalid validation code'
				else if (errMsg.indexOf('request a code again') >= 0 || errMsg.indexOf('xpired') >= 0)
					errMsg = 'This code has expired or is no longer valid. Tap "Resend code" below to get a new one.'
				else if (errMsg.indexOf('Invalid verification code') >= 0)
					// With 'PreventUserExistenceErrors' enabled, Cognito also returns this for an account that is already verified.
					errMsg = 'This code is not valid. Use the code in the latest WAND email, or tap "Resend code" below. If your email is already verified, go back to log in.'
				setError(errMsg)
			} else {
				localStorage.removeItem(CONFIRM_NEXT_KEY)
				// Going home before the auto sign-in completes bounces the user to the login page, so wait for it.
				if (auto_sign_in && await auto_sign_in)
					history.push(redirect_url || HOME_PAGE)
				else if (next == 'resetpwd') {
					// The email is now verified, so the password reset that failed earlier can go ahead.
					const [resetErrors] = await catchErrors(Auth.forgotPassword(username))
					if (resetErrors)
						setError(errors_to_message(resetErrors))
					else {
						localStorage.setItem(SIGNUP_SUB_KEY, username)
						toast.show('Your email is verified. We have just emailed you a code to reset your password.')
						history.push(RESET_PWD_PATH)
					}
				} else {
					toast.show('Your email is verified. You can now log in.')
					history.push(LOGIN_PATH + (redirect_url ? `?redirect=${encodeURIComponent(redirect_url)}` : ''))
				}
			}
		}
	}

	setProcessing(false)
})())

const sendResetPwdEmail = (username:string, history: any, setError:Function, setProcessing: Function, setIncorrectEmail:Function) => catchErrors((async () => {
	if (!username)
		setError(`Email is required`) 
	else if (!validateEmail(username.trim())) {
		setIncorrectEmail(true)
		setError(`Invalid email format`) 
	} else {
		const email = (username||'').toLowerCase().trim()
		// Doc: https://docs.amplify.aws/lib/auth/manageusers/q/platform/js/#forgot-password
		const [userErrors] = await catchErrors(Auth.forgotPassword(email))

		if (userErrors && has_error(userErrors, [UNVERIFIED_EMAIL_ERROR])) {
			// The account exists but its email was never verified, so Cognito refuses the reset. Verify the email
			// first with a new code, then the reset continues automatically (see 'confirmSignup').
			const { sent, error } = await resend_code(email)
			if (sent) {
				store_confirm_context(email, 'resetpwd')
				history.push(`${CONFIRM_CODE_PATH}?reason=unverified`)
			} else
				setError(error)
		} else if (userErrors) {
			let errMsg = userErrors.map((e:any) => e.message).join(' ') || ''
			setError(errMsg)
		}
		else {
			localStorage.setItem(SIGNUP_SUB_KEY, email)
			history.push(RESET_PWD_PATH)
		}
	}

	setProcessing(false)
})())

const resetPwdEmail = (code:string, password:string, confirmPassword:string, email:string, history: any, setError:Function, setProcessing: Function, setIncorrectPwd: Function, setIncorrectPwdConfirm: Function, toast: Toast) => catchErrors((async () => {
	if (!code || !password) {
		if (!code)
			setError(`Code is required`) 
		if (!password)
			setError(`Password is required`) 
		if (!confirmPassword)
			setError(`Confirm password is required`) 
		if (password !== confirmPassword) {
			setIncorrectPwdConfirm(true)
			setError(`Failed to confirm password`) 
		}
	} else if (get_password_issues(password).length) {
		setIncorrectPwd(true)
		setError(`Your password is missing: ${get_password_issues(password).join(', ')}`)
	} else if (password !== confirmPassword) {
		setIncorrectPwdConfirm(true)
		setError(`Failed to confirm password`) 
	} else {
		// 'email' only comes from the link in the reset email (lets the reset work on any device). Otherwise use the
		// email the reset code was just sent to. Never the stored sign-up email, which may belong to another account.
		const username = (email||'').toLowerCase().trim() || localStorage.getItem(SIGNUP_SUB_KEY)
		if (!username)
			setError(`Missing username. Use the original reset password page that appeared after the send reset password email process.`) 
		else {
			// Doc: https://docs.amplify.aws/lib/auth/manageusers/q/platform/js/#forgot-password
			const [userErrors] = await catchErrors(Auth.forgotPasswordSubmit(username, code.trim(), password))

			if (userErrors) {
				let errMsg = userErrors.map((e:any) => e.message).join(' ') || ''
				if (errMsg.indexOf('validation error detected') >= 0 || errMsg.indexOf('validation errors detected') >= 0) {
					if (errMsg.indexOf('alue at \'password\'') >= 0)
						errMsg = 'Invalid password policy'
					else
						errMsg = 'Invalid validation code'
				}
				setError(errMsg)
			}
			else {
				toast.show('Password successfully reset')
				history.push(LOGIN_PATH)
			}
		}
	}

	setProcessing(false)
})())

const resendConfirmSignup = (username:string, history: any, setError:Function, setProcessing: Function) => catchErrors((async () => {
	if (!username)
		setError(`username is required`) 
	else {
		// Doc: https://docs.amplify.aws/lib/auth/emailpassword/q/platform/js/#re-send-sign-up-confirmation-code
		const [userErrors] = await catchErrors(Auth.resendSignUp(username))

		if (userErrors) {
			let errMsg = userErrors.map((e:any) => e.message).join(' ') || ''
			setError(errMsg)
		}
		else {
			store_confirm_context(username, 'login')
			history.push(CONFIRM_CODE_PATH)
		}
	}

	setProcessing(false)
})())

export const LoginSignup = ({ ...props }) => {
	const mode = props.mode || Modes.login
	const { yes:canInstalled, alreadyInstalled } = isInstallable(props.deferredPrompt)
	const disableAddToHomescreen = !canInstalled && alreadyInstalled

	const [title, btnLabel] = mode == Modes.login 
		? ['Log in', 'Log in'] 
		: mode == Modes.signup 
			? ['Sign up', 'Create account'] : mode == Modes.resetpwd 
				? ['Reset password', 'Reset password'] : mode == Modes.forgotpwd 
					? ['Forgot password', 'Send reset password email'] : ['Email verification', 'Confirm']

	const head = <Helmet>
		<title>Wand - {title}</title>
	</Helmet>

	// Manages redirection to login page if user is not authenticated
	const [processing, setProcessing] = useState(false)
	const [remember, setRemember] = useState(false)
	const [accept, setAccept] = useState(false)
	const [error, setError] = useState('')
	const [showPassword, setShowPassword] = useState(false)
	const [incorrectPwd, setIncorrectPwd] = useState(false)
	const [incorrectPwdConfirm, setIncorrectPwdConfirm] = useState(false)
	const [incorrectEmail, setIncorrectEmail] = useState(false)
	const history = useHistory()
	const location = useLocation()
	const search_params = new URLSearchParams(location.search)
	const redirect_url = search_params.get('redirect') || ''
	// 'email' and 'code' are set by the links in the verification and reset emails, 'reason' by the recovery flows.
	const stored_email = localStorage.getItem(SIGNUP_EMAIL_KEY) || ''
	const prefill_email = (search_params.get('email') || stored_email || '').toLowerCase().trim()
	const prefill_code = search_params.get('code') || ''
	const reason = search_params.get('reason') || ''
	const toast = new Toast(...useIonToast(), { duration:5000 })
	const email_ref = useRef<HTMLInputElement>(null)
	const [password, set_password] = useState('')
	const [resend_wait, set_resend_wait] = useState(0)

	useEffect(() => {
		if (resend_wait <= 0)
			return
		const timer = setTimeout(() => set_resend_wait(resend_wait - 1), 1000)
		return () => clearTimeout(timer)
	}, [resend_wait])

	const handle_resend_code = async () => {
		const email = (email_ref.current?.value || prefill_email || '').toLowerCase().trim()
		if (!email || !validateEmail(email)) {
			setIncorrectEmail(true)
			setError('Enter your email address to get a new code')
			return
		}
		setError('')
		set_resend_wait(RESEND_COOLDOWN_SEC)
		const { sent, error } = await resend_code(email)
		if (sent) {
			store_confirm_context(email, localStorage.getItem(CONFIRM_NEXT_KEY) || 'login')
			// Cognito reports success even for verified accounts (see 'resend_code'), hence the hedged wording.
			toast.show('If this email still needs verifying, a new code is on its way. Already verified? Go back to log in.')
		} else
			setError(error)
	}

	const [showInstallAppMessage, closeInstallAppMessage] = useIonModal(InstallAppMessage, {
		onCancel: () => {
			closeInstallAppMessage()
		}
	})

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		if (!processing) {
			setIncorrectPwd(false)
			setIncorrectPwdConfirm(false)
			setIncorrectEmail(false)
			setProcessing(true)

			const data = new FormData(event.currentTarget)
			const username = `${data.get('email')}`
			const password = `${data.get('password')}`
			const confirmPassword = `${data.get('confirm-password')}`
			const code = `${data.get('code')}`

			let errors
			if (mode == Modes.login)
				[errors] = await login(username, password, history, setError, setProcessing, setIncorrectEmail, setIncorrectPwd, redirect_url)
			else if (mode == Modes.signup)
				[errors] = await signup(username, password, confirmPassword, history, setError, setProcessing, setIncorrectEmail, setIncorrectPwd, setIncorrectPwdConfirm, toast, redirect_url)
			else if (mode == Modes.codeconfirm)
				[errors] = await confirmSignup(code, username, history, setError, setProcessing, toast, redirect_url)
			else if (mode == Modes.forgotpwd)
				[errors] = await sendResetPwdEmail(username, history, setError, setProcessing, setIncorrectEmail)
			else if (mode == Modes.resetpwd)
				[errors] = await resetPwdEmail(code, password, confirmPassword, search_params.get('email') || '', history, setError, setProcessing, setIncorrectPwd, setIncorrectPwdConfirm, toast)
			
			if (errors) {
				setProcessing(false)
				setError(errors.map((e:any) => e.message).join(' '))
			}
		}
	}

	const handleCheckChange = (mode:string) => () => {
		if (mode == 'remember')
			setRemember(!remember)
		else if (mode == 'accept')
			setAccept(!accept)
	}

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword)	
	}

	const handleMouseDownPassword = (event: any) => {
		event.preventDefault()
	}

	const addToHomeScreen = async () => {
		if (canInstalled) {
			// Show the install prompt
			props.deferredPrompt.prompt()
			const { outcome } = await props.deferredPrompt.userChoice
			if (outcome != 'dismissed')
				props.deferredPrompt = null
		} else {
			showInstallAppMessage()
		}
	}

	const showEmail = mode == Modes.login || mode == Modes.signup || mode == Modes.forgotpwd || mode == Modes.codeconfirm
	const showPwdRules = mode == Modes.signup || mode == Modes.resetpwd
	const showPwd = mode == Modes.login || mode == Modes.signup || mode == Modes.resetpwd
	const showConfirmPwd = mode == Modes.signup || mode == Modes.resetpwd
	const showCodeConfirm = mode == Modes.codeconfirm || mode == Modes.resetpwd
	// The code page, and the Forgot password page opened by a sign-up with a taken email, start with the email filled in.
	const prefills_email = mode == Modes.codeconfirm || (mode == Modes.forgotpwd && reason == 'exists')
	const emailSendMsg = mode != Modes.codeconfirm
		? 'A verification code has just been emailed to you. Please use that code to reset your password.'
		: reason == 'unverified'
			? 'The email address on this account was never verified. We have just emailed you a new verification code. Enter it below to verify your account.'
			: prefill_code
				? 'Tap "Confirm" to verify your email address.'
				: 'A verification code has just been emailed to you. Please use that code to verify your account.'

	return (<IonPage>
		{head}
		{processing && <ProgressBar/>}
		<ThemeProvider theme={theme}>
			<Grid container component="main" sx={{ height: '100vh' }}>
				<CssBaseline />
				<Grid
					item
					xs={false}
					sm={4}
					md={7}
					sx={{
						backgroundImage: `url(/assets/image/inversion-tower.webp)`,
						backgroundRepeat: 'no-repeat',
						backgroundColor: (t) =>
							t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900],
						backgroundSize: 'cover',
						backgroundPosition: 'center',
					}}
				/>
				<Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square sx={{ overflow:'scroll', height:'100%', position:'relative' }}>
					{/*<div className="logo-beta">Beta program</div>*/}
					{!disableAddToHomescreen && <div className="install-app" onClick={addToHomeScreen}>Add this app to your homescreen</div>}
					<Box
						sx={{
							my: 8,
							mx: 4,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
						}}>
						<Logo style={{ width:'200px', margin:'-20px 0px 30px 0px' }}/>
						<Typography component="h1" variant="h5">{title}</Typography>
						{showCodeConfirm && <Typography component="p" pt="30px">
							{emailSendMsg}
						</Typography> }
						{mode == Modes.forgotpwd && reason == 'exists' && <Typography component="p" pt="30px">
							You already have a WAND account with this email. Tap "Send reset password email" to get a code, or go back to log in.
						</Typography> }
						<Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
							{ showEmail && <TextField
								margin="normal"
								required
								error={incorrectEmail}
								fullWidth
								id="email"
								label="Email Address"
								name="email"
								autoComplete="email"
								autoFocus={!(mode == Modes.codeconfirm && prefill_email)}
								inputRef={email_ref}
								// Ionic keeps this page mounted between visits, so re-key the field to refresh its default value.
								key={prefills_email ? `email-${mode}-${prefill_email}` : 'email'}
								defaultValue={prefills_email ? prefill_email : undefined}
							/>}
							{ showCodeConfirm && <TextField
								margin="normal"
								required
								fullWidth
								name="code"
								label="Code confirmation"
								type="text"
								id="code"
								autoComplete="one-time-code"
								autoFocus={mode == Modes.codeconfirm && !!prefill_email}
								key={`code-${prefill_code}`}
								defaultValue={prefill_code}
							/>}
							{ showPwd && <FormControl variant="outlined" fullWidth sx={{ mt:'16px', mb:'8px' }}>
								<InputLabel htmlFor="outlined-adornment-password" error={incorrectPwd}>Password</InputLabel>
								<OutlinedInput
									required
									error={incorrectPwd}
									fullWidth
									name="password"
									label="Password"
									type={showPassword ? 'text' : 'password'}
									id="password"
									autoComplete={showPwdRules ? 'new-password' : 'current-password'}
									onChange={(e:any) => set_password(e.target.value)}
									endAdornment={
										<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={handleClickShowPassword}
												onMouseDown={handleMouseDownPassword}
												edge="end"
											>
											{showPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</InputAdornment>
									}
								/>
							</FormControl>}
							{ showPwdRules && <PasswordRules password={password}/> }
							{ showConfirmPwd && <FormControl variant="outlined" fullWidth sx={{ mt:'16px', mb:'8px' }}>
								<InputLabel htmlFor="outlined-adornment-password" error={incorrectPwdConfirm}>Confirm Password</InputLabel>
								<OutlinedInput
									required
									fullWidth
									error={incorrectPwdConfirm}
									name="confirm-password"
									label="Confirm password"
									type={showPassword ? 'text' : 'password'}
									id="confirm-password"
									autoComplete="current-password"
									endAdornment={
										<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={handleClickShowPassword}
												onMouseDown={handleMouseDownPassword}
												edge="end"
											>
											{showPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</InputAdornment>
									}
								/>
							</FormControl>}
							{ mode == Modes.login && <FormControlLabel
								control={<Checkbox value="remember" color="primary" onChange={handleCheckChange('remember')} />}
								label="Remember me"
							/>}
							{ mode == Modes.signup && <FormControlLabel
								sx={{ height:'70px', display:'flex', alignItems:'flex-start' }}
								control={<Checkbox value="accept" color="primary" onChange={handleCheckChange('accept')} />}
								label={
								<Box sx={{ marginTop:'9px' }}>
									<span>I accept the </span>
									<Link target="_blank" to={{ pathname:TERMS_PDF_PATH, state:{ origin:'login' }}}>terms of use</Link>
									<span> and </span>
									<Link target="_blank" to={{ pathname:PRIVACY_POLICY_PATH, state:{ origin:'login' }}}>privacy policy</Link>
								</Box>
								}
							/>}
							<Box sx={{ position: 'relative' }}>
								<Button
									type="submit"
									fullWidth
									variant="contained"
									disabled={processing || (mode == Modes.signup && !accept)}
									size="large"
									sx={{ mt: 3, mb: 2 }}
								>{btnLabel}</Button>
								{processing && <CircularProgress
									size={24}
									sx={{
										color: theme.palette.primary.main,
										position: 'absolute',
										top: '50%',
										left: '50%',
										marginTop: '-8px',
										marginLeft: '-8px',
									}}
								/>}
							</Box>
							{ mode == Modes.login && <Grid container>
								<Grid item xs>
									<Link to={FORGOT_PWD_PATH + (redirect_url ? `?redirect=${encodeURIComponent(redirect_url)}` : '')} >Forgot password?</Link>
								</Grid>
								<Grid item>
									<Link to={SIGNUP_PATH + (redirect_url ? `?redirect=${encodeURIComponent(redirect_url)}` : '')} >
										{"Don't have an account? Sign Up"}
									</Link>
								</Grid>
							</Grid>}
							{ mode == Modes.codeconfirm && <Grid container>
								<Grid item xs>
									<Button variant="text" size="small" sx={{ p:0, textTransform:'none' }} disabled={resend_wait > 0} onClick={handle_resend_code}>
										{resend_wait > 0 ? `Code sent. You can resend in ${resend_wait}s` : 'Didn\'t get a code? Resend code'}
									</Button>
								</Grid>
								<Grid item>
									<Link to={LOGIN_PATH + (redirect_url ? `?redirect=${encodeURIComponent(redirect_url)}` : '')} >
										{"Back to log in"}
									</Link>
								</Grid>
							</Grid>}
							{ (mode == Modes.signup || mode == Modes.forgotpwd) && <Grid container>
								<Grid item xs>
									<Link to="#" ></Link>
								</Grid>
								<Grid item>
									<Link to={LOGIN_PATH + (redirect_url ? `?redirect=${encodeURIComponent(redirect_url)}` : '')} >
										{"Already have an account? Log in"}
									</Link>
								</Grid>
							</Grid>}
							{error && <ErrorMessage sx={{ mt: 3 }}>{error}</ErrorMessage>}
						</Box>
						<Copyright />
					</Box>
				</Grid>
			</Grid>
		</ThemeProvider>
	</IonPage>)
}
